import { Dayjs } from '@bemlo/date'

import { CustomRange } from './CustomRange'
import { RangeNavigator } from './RangeNavigator'
import { useDateRangePicker } from './useDateRangePicker'
import { ALL_RANGE_TYPES, RangeType, useRangeTypes } from './useRangeType'
import { useTranslations } from '../../translate'
import { Button } from '../Button'
import { Flex } from '../Flex'
import { PopoverCombobox } from '../Menu/PopoverCombobox'

type DateRangePickerProps = {
  startDate: Dayjs
  endDate: Dayjs | undefined
  rangeType: RangeType
  enabledRangeTypes: RangeType[]
  showToday?: boolean
  setRangeType: (range: RangeType) => void
  onChange: (start: Dayjs, end: Dayjs | undefined) => void
}

const DateRangePicker = (props: DateRangePickerProps) => {
  const t = useTranslations()

  const { selectedOption, options } = useRangeTypes(
    props.rangeType,
    props.enabledRangeTypes,
  )

  const handleClickToday = () => {
    const todayRange = selectedOption?.getToday?.()
    if (todayRange) return props.onChange(...todayRange)

    const today = t.$date()
    // End date should always be defined since today button is disabled when it's not
    if (!props.endDate) return props.onChange(today, today)

    const endDiff = props.endDate.diff(props.startDate.startOf('d'), 'd')
    props.onChange(today, today.add(endDiff, 'day'))
  }

  return (
    <Flex styles={{ height: 'fit-content' }} gap={2}>
      {props.showToday && (
        <Button
          design="white"
          disabled={!props.endDate}
          onClick={handleClickToday}
          size="xs"
        >
          {selectedOption?.todayLabel ?? t.common.today()}
        </Button>
      )}
      {options.length > 1 && (
        <PopoverCombobox<RangeType>
          compact
          closeOnSelect
          value={props.rangeType}
          onSelect={props.setRangeType}
          groups={{ items: options }}
          type="trigger"
          trigger={
            <Button size="xs" design="white">
              {selectedOption?.label}
            </Button>
          }
        />
      )}
      {props.rangeType === 'custom' ? (
        <CustomRange
          startDate={props.startDate.toDate()}
          endDate={props.endDate?.toDate()}
          onChange={(values) =>
            props.onChange(
              values.startDate ? t.$date(values.startDate) : props.startDate,
              values.endDate ? t.$date(values.endDate) : props.endDate,
            )
          }
          startLabel={props.startDate.format('L')}
          endLabel={props.endDate?.format('L')}
        />
      ) : (
        <RangeNavigator
          label={selectedOption?.getLabel?.(props.startDate) ?? ''}
          onChange={(delta) => {
            if (!selectedOption) return
            const newFirstDate = selectedOption.getMovedDate?.(
              props.startDate,
              delta,
            )
            const newLastDate =
              newFirstDate && selectedOption.getLastDate?.(newFirstDate)

            if (!newFirstDate || !newLastDate) return
            props.onChange(newFirstDate, newLastDate)
          }}
        />
      )}
    </Flex>
  )
}

DateRangePicker.Navigator = RangeNavigator

export type { RangeType, DateRangePickerProps }
export { DateRangePicker, useDateRangePicker, ALL_RANGE_TYPES }
